/**
 * Automations API — REST client for /api/automations
 *
 * Mirrors the flowsApi / campaignsApi shape.
 * The detail endpoint returns { automation, nodes, edges } inside the `data`
 * envelope, so ItemResponse wraps that object.
 */

import type { ListResponse, ItemResponse, DeleteResponse } from '../../types/api.js';
import type { Automation, AutomationStatus } from '../../types/models.js';
import { apiRequest } from './core.js';

export interface AutomationNode {
  id: string;
  kind: string;
  toolName?: string;
  primitiveType?: string;
}

export interface AutomationEdge {
  id: string;
  source: string;
  target: string;
}

export interface AutomationDetail {
  automation: Automation;
  nodes: AutomationNode[];
  edges: AutomationEdge[];
}

export interface WorkflowRecipe {
  id: string;
  name: string;
  description: string;
  triggerType: 'SCHEDULE' | 'EVENT' | 'MANUAL';
}

export const automationsApi = {
  /** List all automations for the current workspace with run stats. */
  list: () => apiRequest<ListResponse<Automation>>('/automations?limit=100'),

  /** Get a single automation with its full graph (nodes + edges). */
  get: (id: string) => apiRequest<ItemResponse<AutomationDetail>>(`/automations/${id}`),

  /** Create a DRAFT workflow (canvas "New workflow"). */
  create: (name: string, triggerType: 'MANUAL' | 'SCHEDULE' | 'EVENT' = 'MANUAL') =>
    apiRequest<ItemResponse<Automation>>('/automations', {
      method: 'POST',
      body: JSON.stringify({ name, triggerType }),
    }),

  /** Rename (or update trigger config). */
  rename: (id: string, name: string) =>
    apiRequest<ItemResponse<Automation>>(`/automations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),

  /** Run once now (MANUAL trigger). */
  runNow: (id: string) =>
    apiRequest<ItemResponse<{ id: string; status: string }>>(`/automations/${id}/run`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /** One-click workflow recipes (PR 4). */
  recipes: () => apiRequest<ListResponse<WorkflowRecipe>>('/automations/recipes'),

  /** Materialize a recipe into a DRAFT workflow. */
  installRecipe: (recipeId: string) =>
    apiRequest<ItemResponse<{ automationId: string }>>('/automations/from-recipe', {
      method: 'POST',
      body: JSON.stringify({ recipeId }),
    }),

  /** Toggle ACTIVE or PAUSED. */
  updateStatus: (id: string, status: 'ACTIVE' | 'PAUSED') =>
    apiRequest<ItemResponse<Automation>>(`/automations/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  /** Permanently delete an automation. */
  delete: (id: string) =>
    apiRequest<DeleteResponse>(`/automations/${id}`, { method: 'DELETE' }),
};

// Export the status type for convenience
export type { AutomationStatus };
